import React from "react";
import {
  Box,
  Typography,
  TableRow,
  TableCell,
  Collapse,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  ArrowForward as ArrowForwardIcon,
  Description as DocumentIcon,
} from "@mui/icons-material";
import {
  formatUnderscoreText,
  parseJSONSafely,
  toAbsoluteUrl,
} from "../../../utils/activityLogUtils";
import { renderJSONAsTable } from "./JSONTableRenderer";

/**
 * Expandable row details component
 * Displays old/new values, metadata, documents, and audit info
 */
const ExpandableRowDetails = ({ log, expanded }) => {
  const theme = useTheme();

  const oldValueParsed = parseJSONSafely(log.old_value);
  const newValueParsed = parseJSONSafely(log.new_value);
  const metadataParsed = parseJSONSafely(log.metadata);

  return (
    <TableRow>
      <TableCell
        colSpan={8}
        sx={{
          py: 0,
          px: 0,
          borderBottom: "none",
          bgcolor: theme.palette.grey[50],
        }}
      >
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              py: 3,
              px: 3,
              bgcolor: theme.palette.background.paper,
              borderRadius: 1,
              border: `1px solid ${theme.palette.grey[300]}`,
              m: 2,
            }}
          >
            {/* Field Name */}
            {log.field_name && (
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontSize: "1rem",
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                }}
              >
                Field:{" "}
                {formatUnderscoreText(log.field_name).replace(/\b\w/g, (l) =>
                  l.toUpperCase()
                )}
              </Typography>
            )}

            {/* Value Changes */}
            {(oldValueParsed !== null || newValueParsed !== null) && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, color: "text.primary", mb: 2 }}
                >
                  Value Changes
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md:
                        oldValueParsed && newValueParsed
                          ? "1fr 40px 1fr"
                          : "1fr",
                    },
                    gap: 2,
                    alignItems: "start",
                  }}
                >
                  {oldValueParsed !== null && (
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, mb: 1, color: "error.main" }}
                      >
                        Previous Value
                      </Typography>
                      {renderJSONAsTable(
                        oldValueParsed,
                        "Old Value",
                        "error",
                        theme
                      )}
                    </Box>
                  )}

                  {oldValueParsed !== null && newValueParsed !== null && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        pt: 4,
                      }}
                    >
                      <ArrowForwardIcon
                        sx={{ color: theme.palette.text.secondary }}
                      />
                    </Box>
                  )}

                  {newValueParsed !== null && (
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, mb: 1, color: "success.main" }}
                      >
                        New Value
                      </Typography>
                      {renderJSONAsTable(
                        newValueParsed,
                        "New Value",
                        "success",
                        theme
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* Document and Metadata */}
            {(log.document_url || metadataParsed) && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md:
                      log.document_url && metadataParsed ? "300px 1fr" : "1fr",
                  },
                  gap: 3,
                  mb: 3,
                }}
              >
                {log.document_url && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, mb: 1.5, color: "text.primary" }}
                    >
                      Document
                    </Typography>
                    <Chip
                      label={formatUnderscoreText(
                        log.document_name || log.document_url
                      )}
                      size="medium"
                      color="primary"
                      clickable
                      onClick={() =>
                        window.open(toAbsoluteUrl(log.document_url), "_blank")
                      }
                      icon={<DocumentIcon />}
                    />
                  </Box>
                )}

                {metadataParsed && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, mb: 1.5, color: "text.primary" }}
                    >
                      Additional Details
                    </Typography>
                    {renderJSONAsTable(
                      metadataParsed,
                      "Metadata",
                      "default",
                      theme
                    )}
                  </Box>
                )}
              </Box>
            )}

            {/* Audit Information */}
            {(log.current_step || log.ip_address || log.user_agent) && (
              <Box
                sx={{
                  mt: 3,
                  pt: 2,
                  borderTop: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, mb: 1.5, color: "text.primary" }}
                >
                  Audit Information
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, 1fr)",
                      lg: "repeat(3, 1fr)",
                    },
                    gap: 2,
                    alignItems: "start",
                  }}
                >
                  {log.current_step && (
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          display: "block",
                          mb: 0.5,
                        }}
                      >
                        Workflow Step
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatUnderscoreText(log.current_step)}
                      </Typography>
                    </Box>
                  )}
                  {log.ip_address && (
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          display: "block",
                          mb: 0.5,
                        }}
                      >
                        IP Address
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {log.ip_address}
                      </Typography>
                    </Box>
                  )}
                  {log.user_agent && (
                    <Box sx={{ gridColumn: { xs: "1", sm: "1 / -1" } }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          display: "block",
                          mb: 0.5,
                        }}
                      >
                        User Agent
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, wordBreak: "break-all" }}
                      >
                        {log.user_agent}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
};

export default ExpandableRowDetails;
